import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Layout from '@shared/ui/organisms/layout/Layout';
import { HeadlineMedium, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { LinkButton } from '@shared/ui/atoms/buttons/LinkButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Colours from '@shared/ui/atoms/Colours';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { selectOrphanedUsers } from '@features/settings/store/management.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { Permission } from '@shared/models/user.models';
import { AddAnotherTeamButton } from '@shared/ui/atoms/buttons/AddAnotherTeamButton';
import LookingAfterMultipleTeams from './components/LookingAfterMultiTeams';
import { canManageWorkGroups, isBasicUser, isCompanyAdminUser } from '@shared/utils/AccessControl';
import AccessRequired from '../components/AccessRequired';
import { selectAllTeams } from '@shared/store/config.duck';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { SidebarItem } from '../office-management-page-v2/components/SidebarItem';
import { getParameterByName, setQueryParams } from '@shared/utils/UrlUtils';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';
import { DepartmentContentCard } from '../department-management-page/components/DepartmentContentCard';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { fetchTags, selectAllTags } from '@shared/store/userTags.duck';
import { TagWithCount } from '@shared/models/tags.models';
import { EditWorkGroupView } from '../tag-management-page/EditWorkGroupView';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { EditTeamView } from '../EditTeamPage';
import { EditDepartmentView } from '../department-management-page/EditDepartmentPage';

enum ManagePanel {
  Teams = 'teams',
  WorkGroups = 'workgroups',
  Departments = 'departments',
}

export default function ManageTeamsAndUsersPage() {
  const {t} = useTranslation();
  const [activePanel, setActivePanel] = useState<ManagePanel>(() => {
    const qsPanel = getParameterByName('panel', window.location.href);
    return Object.values(ManagePanel).includes(qsPanel as ManagePanel) ? qsPanel as ManagePanel : ManagePanel.Teams;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { view: ManagePanel.Teams, icon: IconTypes.Users, label: 'settings-page.teams-tab' },
    { view: ManagePanel.WorkGroups, icon: IconTypes.Profile, label: 'settings-page.workgroups-tab' },
    { view: ManagePanel.Departments, icon: IconTypes.Office, label: 'settings-page.departments-tab' },
  ];

  const activeItem = menuItems.find(item => item.view === activePanel) ?? menuItems[0];

  const onPanelChange = (panel: ManagePanel) => {
    setActivePanel(panel);
    setQueryParams([`panel=${panel}`]);
    setMobileMenuOpen(false);
  };

  return (
    <Layout fullWidth={true} hideFooter={true}>
      <RibbonContainer>
        <RibbonContentContainer>
          <GoBackButton url={'/manage'} />
          <HeadlineMedium>{t('settings-page.manage-teams')}</HeadlineMedium>
        </RibbonContentContainer>
      </RibbonContainer>

      <ContentContainer>
        <Sidebar>
          <MobileMenuToggle onClick={() => setMobileMenuOpen(open => !open)}
                            aria-expanded={mobileMenuOpen}
                            type="button">
            <MobileMenuToggleLabel>
              <Icon icon={activeItem.icon} />
              <BodyRegular weight={600}>{t(activeItem.label)}</BodyRegular>
            </MobileMenuToggleLabel>
            <Icon icon={mobileMenuOpen ? IconTypes.Up : IconTypes.Down} />
          </MobileMenuToggle>
          <SidebarItems $open={mobileMenuOpen}>
            {menuItems.map(item => (
              <SidebarItem key={item.view}
                           icon={item.icon}
                           label={item.label}
                           active={activePanel === item.view}
                           onClick={() => onPanelChange(item.view)} />
            ))}
          </SidebarItems>
        </Sidebar>
        <MainContent>
          {activePanel === ManagePanel.Teams && <TeamsPanel />}
          {activePanel === ManagePanel.WorkGroups && <WorkGroupsPanel />}
          {activePanel === ManagePanel.Departments && <DepartmentsPanel />}
        </MainContent>
      </ContentContainer>
    </Layout>
  );
}

function TeamsPanel() {
  const current = useSelector(selectCurrentUser);
  const company = current?.companyEntity;
  const teams = useSelector(selectAllTeams);
  const orphanedUsers = useSelector(selectOrphanedUsers);
  const hasAccessToManageTeams = useMemo(() => isBasicUser(current), [current]);
  const [editingTeamId, setEditingTeamId] = useState<string | undefined>(undefined);
  const {t} = useTranslation();

  const myTeam = useMemo(() => teams.filter(t => t.id === current?.teamEntity?.id), [teams, current]);
  const otherTeams = useMemo(() => teams.filter(t => t.id !== current?.teamEntity?.id), [teams, current]);

  if (editingTeamId) {
    return <EditTeamView teamId={editingTeamId} onClose={() => setEditingTeamId(undefined)} />;
  }

  return (
    <React.Fragment>
      <HeadlineSmall style={{marginBottom: 24}}>{t('settings-page.teams-tab')}</HeadlineSmall>
      <div className="manage__sections">

        <AccessRequired visible={hasAccessToManageTeams} companyAdminsRequired={true} teamAdminsRequired={true}  />

        {orphanedUsers.length > 0 && (current?.role === Permission.CompanyAdmin || current?.role === Permission.TeamAdmin) ?
          <React.Fragment>
            <BodyLarge className="manage__subtitle" colour={Colours.red}>{t('settings.users-with-no-teams')}</BodyLarge>
            <div className="manage__section">
              <BodyRegular>{`${orphanedUsers.length}`} {t('users')}</BodyRegular>
              <LinkButton className="management__button"
                          text="button.assign-users-to-team"
                          type="grey"
                          link="/manage/assign-users"/>
            </div>
          </React.Fragment>
          : null}

        <BodyLarge className="manage__subtitle">{t('settings.my-team')}</BodyLarge>
        {myTeam.map((teamInfo, key) => (
          <TeamSection key={key} teamInfo={teamInfo} onEdit={setEditingTeamId}/>
        ))}

        <BodyLarge className="manage__subtitle">{t('settings.other-teams')}</BodyLarge>
        {otherTeams.map((teamInfo, key) => (
          <TeamSection key={key} teamInfo={teamInfo} onEdit={setEditingTeamId}/>
        ))}

        {!company?.enabled && <LookingAfterMultipleTeams/>}

      </div>
      {company?.enabled && <AddAnotherTeamButton size={ButtonSize.Medium} fullWidth={true}/>}
    </React.Fragment>
  );
}

function WorkGroupsPanel() {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const hasAccess = useMemo(() => canManageWorkGroups(currentUser), [currentUser]);
  const workGroups = useSelector(selectAllTags);
  const [editingWorkGroupId, setEditingWorkGroupId] = useState<number | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchTags());
  }, [dispatch]);

  const addNewWorkGroup = () => {
    dispatch(openDialogWithPayload({
      payload: { },
      activeDialog: DialogIdentifiers.CreateTagDialog
    }));
  }

  if (editingWorkGroupId !== undefined) {
    return <EditWorkGroupView workGroupId={editingWorkGroupId} onClose={() => setEditingWorkGroupId(undefined)} />;
  }

  return (
    <React.Fragment>
      <HeadlineSmall style={{marginBottom: 24}}>{t('settings-page.workgroups-tab')}</HeadlineSmall>
      <AccessRequired teamAdminsRequired={true} companyAdminsRequired={true} visible={!hasAccess} />

      <div className="manage__sections">
        {workGroups.length === 0 &&
          <BodyRegular style={{textAlign: 'center', marginBottom: 24}}>
            {t('settings-page.no-workgroups-message')}
          </BodyRegular>}
        {workGroups.map((workGroup: TagWithCount) => (
          <WorkGroupContentCard key={workGroup.id}
                                workGroup={workGroup}
                                onClick={wg => setEditingWorkGroupId(wg.id)} />
        ))}
      </div>

      <PrimaryButton size={ButtonSize.Medium}
                     fullWidth={true}
                     click={addNewWorkGroup}
                     text={'button.add-new-tag'} />
    </React.Fragment>
  );
}

function WorkGroupContentCard(props: { workGroup: TagWithCount, onClick: (workGroup: TagWithCount) => void }) {
  const {t} = useTranslation();
  return (
    <TeamContentCard>
      <BodyRegular weight={600} colour={Colours.blue} style={{marginBottom: 10}}>{props.workGroup.label}</BodyRegular>
      <BodyRegular style={{marginBottom: 16}}>{props.workGroup.memberCount ?? 0} {t('general.team-members')}</BodyRegular>
      <OutlineButton text="button.edit-and-manage-workgroup"
                     className="management__button"
                     style={{maxWidth: 242}}
                     click={() => props.onClick(props.workGroup)} />
    </TeamContentCard>
  )
}

function DepartmentsPanel() {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const current = useSelector(selectCurrentUser);
  const company = current?.companyEntity;
  const departments = useSelector(selectAllDepartments);
  const hasAccessToManageDepartments = useMemo(() => isCompanyAdminUser(current), [current]);
  const [editingDepartmentId, setEditingDepartmentId] = useState<number | undefined>(undefined);

  const addNewDepartment = () => {
    dispatch(openDialogWithPayload({
      payload: { },
      activeDialog: DialogIdentifiers.EditDepartmentDialog
    }))
  }

  if (editingDepartmentId !== undefined) {
    return <EditDepartmentView departmentId={editingDepartmentId} onClose={() => setEditingDepartmentId(undefined)} />;
  }

  return (
    <React.Fragment>
      <HeadlineSmall style={{marginBottom: 24}}>{t('settings-page.departments-tab')}</HeadlineSmall>
      <div className="manage__sections">

        <AccessRequired visible={!hasAccessToManageDepartments} companyAdminsRequired={true} />

        {departments.map((department: Department) => (
          <DepartmentContentCard key={department.id}
                                 department={department}
                                 onClick={d => setEditingDepartmentId(d.id)} />
        ))}

      </div>
      {company?.enabled && <PrimaryButton size={ButtonSize.Medium}
                                          fullWidth={true}
                                          click={addNewDepartment}
                                          text={'settings.departments.add-new'} />}
    </React.Fragment>
  );
}

function TeamSection(props: { teamInfo: any, onEdit: (teamId: string) => void }) {
  const {teamInfo, onEdit} = props;
  const teamName = teamInfo.name;
  const teamButtonClass = useMemo(() => 'manage__section team-' + teamName.replace(' ', '-'), [teamName]);
  const {t} = useTranslation();
  return (
    <TeamContentCard className={teamButtonClass}>
      <BodyRegular weight={600} colour={Colours.blue} style={{marginBottom: 10}}>{teamName}</BodyRegular>
      <BodyRegular className="manage__teamMemberCount" style={{marginBottom: 16}}>{teamInfo.memberCount} {t('general.team-members')}</BodyRegular>
      <OutlineButton text="button.edit-and-manage-team"
                     className="management__button"
                     style={{maxWidth: 242}}
                     click={() => onEdit(teamInfo.id)}/>
    </TeamContentCard>
  )
}

const TeamContentCard = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${Colours.mildGrey};
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 24px 24px 24px;
`

const Sidebar = styled.div`
  width: 100%;
  @media (${tablet}) {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    margin: 0;
  }
`

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: white;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  cursor: pointer;
  i {
    margin-right: 10px;
  }
  @media (${tablet}) {
    display: none;
  }
`

const MobileMenuToggleLabel = styled.div`
  display: flex;
  align-items: center;
`

const SidebarItems = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'block' : 'none'};
  padding: 16px 16px 12px 16px;
  @media (${tablet}) {
    display: block;
    padding: 0;
  }
`

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 500px;
  background-color: white;
  padding: 32px 16px 0 16px;
  @media (${tablet}) {
    padding: 48px 120px;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: ${Colours.veryLightGrey};
  @media (${tablet}) {
    padding: 0 16px;
    background-color: unset;
    display: flex;
    margin: 24px auto 0 auto;
  }
`

const RibbonContainer = styled.div`
  background-color: white;
  padding: 0 16px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  padding: 16px 0;
  @media (${tablet}) {
    padding: 16px 0;
  }
`
